import { useEffect, useRef, useState } from 'react';
import { SITE_CONFIG } from '../../../config/constants';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [isVisible, setIsVisible] = useState(false);
  const [linksVisible, setLinksVisible] = useState(false);
  const [bottomVisible, setBottomVisible] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);
  const linksRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const createObs = (setter: (v: boolean) => void, opts?: { threshold?: number; rootMargin?: string }) => {
      return new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setter(true); },
        { threshold: 0.15, rootMargin: '0px 0px -30px 0px', ...opts }
      );
    };

    const o1 = createObs(setIsVisible);
    const o2 = createObs(setLinksVisible, { threshold: 0.1 });
    const o3 = createObs(setBottomVisible);

    if (topRef.current) o1.observe(topRef.current);
    if (linksRef.current) o2.observe(linksRef.current);
    if (bottomRef.current) o3.observe(bottomRef.current);

    return () => { o1.disconnect(); o2.disconnect(); o3.disconnect(); };
  }, []);

  return (
    <footer className="relative bg-gradient-to-b from-[#0a0a0a] to-[#050505] overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      {/* Top Golden Border */}
      <div className="relative h-px w-full">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-500/40 to-transparent"></div>
        <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-24 h-2 bg-amber-500/20 blur-md"></div>
      </div>

      {/* Decorative Corner Elements */}
      <div className={`absolute top-8 left-8 w-16 h-16 border-l border-t border-amber-500/10 rounded-tl-lg transition-all duration-[1000ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`} style={{ transitionDelay: '300ms' }}></div>
      <div className={`absolute top-8 right-8 w-16 h-16 border-r border-t border-amber-500/10 rounded-tr-lg transition-all duration-[1000ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`} style={{ transitionDelay: '400ms' }}></div>

      {/* Main Footer Content */}
      <div className="relative max-w-7xl mx-auto px-8 pt-20 pb-12">
        {/* Top Section - Brand & CTA */}
        <div ref={topRef} className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 mb-16">
          {/* Brand */}
          <div className={`flex items-center gap-4 transition-all duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="relative">
              <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 border-r border-b border-amber-500/50"></div>
            </div>
            <div>
              <h4 className="text-xl font-light text-white tracking-wide font-display">
                INTERNATIONAL-INTELLIGENCE
              </h4>
              <div className="flex items-center gap-2 mt-1">
                <div className={`h-px bg-gradient-to-r from-amber-500/50 to-transparent transition-all duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${isVisible ? 'w-8' : 'w-0'}`} style={{ transitionDelay: '400ms' }}></div>
                <p className={`text-[10px] tracking-[0.25em] text-amber-500/70 uppercase font-mono transition-all duration-[1000ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '500ms' }}>.ORG Domain</p>
              </div>
            </div>
          </div>

          {/* Status Badge */}
          <div className={`flex items-center gap-4 transition-all duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} style={{ transitionDelay: '200ms' }}>
            <div className="hidden lg:flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-amber-500/5 to-transparent border border-amber-500/20 rounded-full">
              <div className="relative">
                <div className="w-2.5 h-2.5 bg-emerald-400 rounded-full"></div>
                <div className="absolute inset-0 w-2.5 h-2.5 bg-emerald-400 rounded-full animate-ping opacity-50"></div>
              </div>
              <span className="text-xs tracking-[0.15em] text-amber-400/90 uppercase font-medium font-mono">Available for Acquisition</span>
            </div>
            <button 
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="group relative px-6 py-3 bg-gradient-to-r from-amber-600 to-amber-500 rounded-lg overflow-hidden cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-amber-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <span className="relative text-sm font-normal text-black tracking-wide font-other whitespace-nowrap">Make an Offer</span>
            </button>
          </div>
        </div>

        {/* Middle Section - Links Grid */}
        <div ref={linksRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 mb-16">
          {/* About */}
          <div className={`lg:col-span-1 transition-all duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${linksVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: '0ms' }}>
            <div className="flex items-center gap-2 mb-5">
              <div className={`w-1 bg-gradient-to-b from-amber-500 to-amber-500/20 rounded-full transition-all duration-[800ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${linksVisible ? 'h-4' : 'h-0'}`} style={{ transitionDelay: '200ms' }}></div>
              <h5 className="text-xs tracking-[0.2em] text-white/70 uppercase font-medium font-mono">About</h5>
            </div>
            <p className="text-sm text-white/40 leading-relaxed font-body">
              A premium .ORG domain representing authority and trust in the global intelligence sector. Ideal for organizations seeking instant credibility.
            </p>
          </div>

          {/* Navigation */}
          <div className={`transition-all duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${linksVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: '120ms' }}>
            <div className="flex items-center gap-2 mb-5">
              <div className={`w-1 bg-gradient-to-b from-amber-500 to-amber-500/20 rounded-full transition-all duration-[800ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${linksVisible ? 'h-4' : 'h-0'}`} style={{ transitionDelay: '320ms' }}></div>
              <h5 className="text-xs tracking-[0.2em] text-white/70 uppercase font-medium font-mono">Navigation</h5>
            </div>
            <ul className="space-y-3">
              {[
                { label: 'Home', id: 'home' },
                { label: 'Overview', id: 'overview' },
                { label: 'Value Proposition', id: 'value' },
                { label: 'Industries', id: 'industries' },
                { label: 'Contact', id: 'contact' }
              ].map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' })}
                    className="group flex items-center gap-2 text-sm text-white/40 hover:text-amber-400 transition-all duration-300 cursor-pointer font-body"
                  >
                    <span className="w-0 group-hover:w-3 h-px bg-amber-500/50 transition-all duration-300"></span>
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className={`transition-all duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${linksVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: '240ms' }}>
            <div className="flex items-center gap-2 mb-5">
              <div className={`w-1 bg-gradient-to-b from-amber-500 to-amber-500/20 rounded-full transition-all duration-[800ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${linksVisible ? 'h-4' : 'h-0'}`} style={{ transitionDelay: '440ms' }}></div>
              <h5 className="text-xs tracking-[0.2em] text-white/70 uppercase font-medium font-mono">Contact Owner</h5>
            </div>
            <ul className="space-y-3">
              <li>
                <a 
                  href={`mailto:${SITE_CONFIG.contact.email}`}
                  className="group flex items-center gap-3 text-sm text-white/40 hover:text-amber-400 transition-all duration-300 cursor-pointer font-body"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 group-hover:bg-amber-500/10 border border-white/5 group-hover:border-amber-500/20 transition-all duration-300">
                    <i className="ri-mail-line text-amber-500/60 group-hover:text-amber-400 text-sm transition-colors"></i>
                  </div>
                  <span>{SITE_CONFIG.contact.email}</span>
                </a>
              </li>
              <li>
                <a 
                  href="https://bronzyazilim.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 text-sm text-white/40 hover:text-amber-400 transition-all duration-300 cursor-pointer font-body"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 group-hover:bg-amber-500/10 border border-white/5 group-hover:border-amber-500/20 transition-all duration-300">
                    <i className="ri-global-line text-amber-500/60 group-hover:text-amber-400 text-sm transition-colors"></i>
                  </div>
                  <span>bronzyazilim.com</span>
                </a>
              </li>
              <li>
                <div className="flex items-center gap-3 text-sm text-white/40 font-body">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 border border-white/5">
                    <i className="ri-map-pin-line text-amber-500/60 text-sm"></i>
                  </div>
                  <span>Ankara, Turkey</span>
                </div>
              </li>
            </ul>
          </div>

          {/* Domain Info */}
          <div className={`transition-all duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${linksVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: '360ms' }}>
            <div className="flex items-center gap-2 mb-5">
              <div className={`w-1 bg-gradient-to-b from-amber-500 to-amber-500/20 rounded-full transition-all duration-[800ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${linksVisible ? 'h-4' : 'h-0'}`} style={{ transitionDelay: '560ms' }}></div>
              <h5 className="text-xs tracking-[0.2em] text-white/70 uppercase font-medium font-mono">Domain Details</h5>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-white/5">
                <span className="text-xs text-white/30 uppercase tracking-wider font-mono">Extension</span>
                <span className="text-sm text-white/60 font-body">.ORG</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-white/5">
                <span className="text-xs text-white/30 uppercase tracking-wider font-mono">Status</span>
                <span className="text-sm text-emerald-400 font-body">Active</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-xs text-white/30 uppercase tracking-wider font-mono">Type</span>
                <span className="text-sm text-amber-400 font-body">Premium</span>
              </div>
            </div>
          </div>
        </div>

        {/* Divider with Center Ornament */}
        <div ref={bottomRef} className="relative flex items-center justify-center mb-8">
          <div className={`flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-white/10 transition-all duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${bottomVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`} style={{ transformOrigin: 'right' }}></div>
          <div className={`mx-6 flex items-center gap-2 transition-all duration-[800ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${bottomVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`} style={{ transitionDelay: '300ms' }}>
            <div className="w-1 h-1 bg-amber-500/40 rounded-full"></div>
            <div className="w-2 h-2 bg-amber-500/30 rounded-full border border-amber-500/30"></div>
            <div className="w-1 h-1 bg-amber-500/40 rounded-full"></div>
          </div>
          <div className={`flex-1 h-px bg-gradient-to-l from-transparent via-white/10 to-white/10 transition-all duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${bottomVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`} style={{ transformOrigin: 'left' }}></div>
        </div>

        {/* Bottom Bar */}
        <div className={`flex flex-col md:flex-row items-center justify-between gap-4 transition-all duration-[1000ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${bottomVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: '200ms' }}>
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-4 gap-y-2">
            <p className="text-xs text-white/30 font-body">
              &copy; {currentYear} {SITE_CONFIG.owner.name}
            </p>
            <span className="hidden md:inline text-white/10">&bull;</span>
            <p className="text-xs text-white/20 font-body">
              All rights reserved
            </p>
            <span className="hidden md:inline text-white/10">&bull;</span>
            <p className="text-xs text-white/20 font-body">
              Premium Domain Asset
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Golden Accent Bar */}
      <div className="relative h-1">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-500/40 to-transparent"></div>
        <div className="absolute left-1/2 -translate-x-1/2 bottom-0 w-32 h-4 bg-amber-500/20 blur-xl"></div>
      </div>

      {/* Bottom Corner Elements */}
      <div className={`absolute bottom-8 left-8 w-12 h-12 border-l border-b border-amber-500/10 rounded-bl-lg transition-all duration-[1000ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${bottomVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`} style={{ transitionDelay: '400ms' }}></div>
      <div className={`absolute bottom-8 right-8 w-12 h-12 border-r border-b border-amber-500/10 rounded-br-lg transition-all duration-[1000ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${bottomVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`} style={{ transitionDelay: '500ms' }}></div>
    </footer>
  );
}
